/**
 * Where Help goes — the registry behind the Help button (4.1.56).
 *
 * THE UI EXISTS BEFORE THE DESTINATIONS DO, and that is deliberate: the
 * button and its menu are on every screen now, so a person learns where
 * help lives, and each entry is connected here as its destination comes
 * to exist. An entry without a destination is shown and marked "Soon",
 * exactly as the sidebar marks the Reviews screen — it is never a page
 * that pretends to be finished. NOTHING HERE INVENTS A PAGE: an entry
 * links only to something that already exists (the homepage's own "How
 * it works" section, the console's setup checklist, the recorded
 * support address) or to nothing.
 *
 * HELP & SUPPORT, CONTACT SUPPORT AND REPORT A PROBLEM ARE THE SUPPORT
 * TICKETS (0058). On the console they open the Help & Support screen
 * (`/support`, the person's own requests and a form that already knows
 * who they are); on the public site they open `/support`, the contact
 * page for somebody who is not a customer yet. Both write the same
 * ticket. `legalSupportEmail` in `legal-identity.ts` is still the
 * address the published policies name, and is no longer what these
 * entries link to: a mailto: was a door with nothing behind it.
 *
 * CONTEXTUAL HELP IS WIRED, NOT WRITTEN. `HELP_TOPICS` names a topic for
 * each console screen and `helpTopicForPath` maps a route to it, so a
 * "Help with Payments" entry appears at the top of the menu the moment
 * that topic is given a destination — and not before. No knowledge base
 * is built here; the map is the architecture for one.
 */

export type HelpSurface = 'site' | 'console';

export interface HelpLink {
  key: 'help_support' | 'getting_started' | 'how_bookings_work' | 'contact_support' | 'report_problem' | 'topic';
  label: string;
  /** Absent while the destination does not exist yet: shown as "Soon". */
  href?: string;
  /** Leaves the app (a mail client, another site): opened accordingly. */
  external?: boolean;
}

/** The help centre, when there is one. Null today: nothing is linked to a page that does not exist. */
export const HELP_CENTER_URL: string | null = null;

export type HelpTopicKey =
  | 'support'
  | 'today' | 'calendar' | 'bookings' | 'availability' | 'customers' | 'payments' | 'services'
  | 'team' | 'locations' | 'resources' | 'booking_page' | 'businesses' | 'industries' | 'preferences'
  | 'messaging' | 'marketing' | 'forms' | 'service_requests' | 'check_in' | 'reviews' | 'ordering' | 'classes' | 'waitlist'
  | 'reports' | 'plan' | 'settings' | 'integrations' | 'policies' | 'status';

export interface HelpTopic {
  /** The subject as the menu names it: "Help with {label}". */
  label: string;
  /** Where "Help with …" goes. Absent until an article exists. */
  href?: string;
}

/** One topic per console screen. Add the href when the article exists; the entry appears by itself. */
export const HELP_TOPICS: Record<HelpTopicKey, HelpTopic> = {
  support: { label: 'Help & Support', href: '/support' },
  today: { label: 'Today' },
  calendar: { label: 'the Calendar' },
  bookings: { label: 'Bookings' },
  availability: { label: 'Availability' },
  customers: { label: 'Customers' },
  payments: { label: 'Payments' },
  services: { label: 'Services' },
  team: { label: 'your Team' },
  locations: { label: 'Locations' },
  resources: { label: 'Rooms & equipment' },
  booking_page: { label: 'your Booking page' },
  businesses: { label: 'Businesses & booking pages' },
  industries: { label: 'Trades & extra pages' },
  preferences: { label: 'Holidays & preferences' },
  messaging: { label: 'Messages' },
  marketing: { label: 'Marketing' },
  forms: { label: 'Intake forms' },
  service_requests: { label: 'Service requests' },
  check_in: { label: 'Check-in' },
  reviews: { label: 'Reviews' },
  ordering: { label: 'Products & orders' },
  classes: { label: 'Classes' },
  waitlist: { label: 'the Waitlist' },
  reports: { label: 'Reports' },
  plan: { label: 'Plan & billing' },
  settings: { label: 'Settings' },
  integrations: { label: 'Connected calendars & integrations' },
  policies: { label: 'Booking policies' },
  status: { label: 'Status' },
};

/** Console routes (without the /admin basename) to their topic. First match by prefix wins. */
const ROUTE_TOPICS: [string, HelpTopicKey][] = [
  ['/support', 'support'],
  ['/appointments', 'bookings'],
  ['/calendar-connections', 'integrations'],
  ['/calendar', 'calendar'],
  ['/balances', 'payments'],
  ['/payments-setup', 'payments'],
  ['/customers', 'customers'],
  ['/services', 'services'],
  ['/providers', 'team'],
  ['/resources', 'resources'],
  ['/locations', 'locations'],
  ['/availability', 'availability'],
  ['/booking-page', 'booking_page'],
  ['/booking-policies', 'policies'],
  ['/businesses', 'businesses'],
  ['/industries', 'industries'],
  ['/experience', 'preferences'],
  ['/notifications', 'messaging'],
  ['/marketing', 'marketing'],
  ['/forms', 'forms'],
  ['/service-requests', 'service_requests'],
  ['/check-in', 'check_in'],
  ['/reviews', 'reviews'],
  ['/ordering', 'ordering'],
  ['/classes', 'classes'],
  ['/waitlist', 'waitlist'],
  ['/reports', 'reports'],
  ['/plan', 'plan'],
  ['/settings', 'settings'],
  ['/integrations', 'integrations'],
  ['/status', 'status'],
  ['/health', 'status'],
];

/** The topic for a console path, or null for a screen with none (the setup checklist, a 404). */
export function helpTopicForPath(pathname: string): HelpTopicKey | null {
  const path = pathname.replace(/^\/admin(?=\/|$)/, '') || '/';
  if (path === '/') return 'today';
  for (const [prefix, topic] of ROUTE_TOPICS) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return topic;
  }
  return null;
}

export interface HelpContext {
  /** Which surface the menu is on: the marketing site or the owner's console. */
  surface: HelpSurface;
  /** The recorded support address, or null while none is recorded. */
  supportEmail: string | null;
  /** Shown in a problem report so support knows which build it was. */
  version?: string;
  /** The screen the report was sent from — a path, never data. */
  screen?: string;
  /** The current screen's topic, for the contextual entry. */
  topic?: HelpTopicKey | null;
  /** Override the topic registry (tests). */
  topics?: Record<HelpTopicKey, HelpTopic>;
}

/** Where the support screen is on each surface (0058). The same ticket system behind both. */
export const SUPPORT_PATHS: Record<HelpSurface, string> = { site: '/support', console: '/support' };

function supportHref(surface: HelpSurface, params: Record<string, string>): string {
  const q = new URLSearchParams(params).toString();
  return q ? `${SUPPORT_PATHS[surface]}?${q}` : SUPPORT_PATHS[surface];
}

/** The menu's entries, in order, for one surface and one moment. */
export function helpLinks(context: HelpContext): HelpLink[] {
  const { surface, screen } = context;
  const links: HelpLink[] = [];
  const topics = context.topics ?? HELP_TOPICS;
  const topic = context.topic ? topics[context.topic] : null;
  // The screen's own topic; on the support screen itself the entry
  // would repeat the next one, so it is left out there.
  if (topic?.href && context.topic !== 'support') links.push({ key: 'topic', label: `Help with ${topic.label}`, href: topic.href, external: /^https?:/.test(topic.href) });
  links.push({ key: 'help_support', label: 'Help & Support', ...(HELP_CENTER_URL ? { href: HELP_CENTER_URL, external: true } : { href: SUPPORT_PATHS[surface] }) });
  // The console's own setup checklist is getting started; the site has no
  // page for it yet.
  links.push({ key: 'getting_started', label: 'Getting started', ...(surface === 'console' ? { href: '/#getting-started' } : {}) });
  // The homepage explains how bookings work in its own section.
  links.push({ key: 'how_bookings_work', label: 'How bookings work', ...(surface === 'site' ? { href: '/#how-it-works' } : {}) });
  // A support request, with the form open; a problem report says which
  // screen it came from so the person does not have to.
  links.push({ key: 'contact_support', label: 'Contact support', href: supportHref(surface, { new: '1' }) });
  links.push({ key: 'report_problem', label: 'Report a problem', href: supportHref(surface, { new: '1', category: 'technical_problem', ...(screen ? { screen } : {}) }) });
  return links;
}
